import { ApiError, api, type StreamHealthDto } from '../api.js';
import { clear, el, formatBytes, toast } from '../ui.js';

/** Settings: stream link, stream health, disk usage, log out everywhere. */

const healthText = (health: StreamHealthDto | null): string => {
  if (health === null) return 'Checking…';
  const when = new Date(health.checkedAt).toLocaleTimeString();
  return `${health.ok ? 'Stream OK' : 'Stream problem'} — ${health.detail} (checked ${when})`;
};

const asHealth = (value: unknown): StreamHealthDto | null => {
  if (typeof value !== 'object' || value === null) return null;
  const candidate = value as Partial<StreamHealthDto>;
  if (typeof candidate.ok !== 'boolean' || typeof candidate.checkedAt !== 'number') return null;
  return { ok: candidate.ok, checkedAt: candidate.checkedAt, detail: String(candidate.detail ?? '') };
};

export const renderSettings = (root: HTMLElement): (() => void) => {
  clear(root);

  const healthPill = el('p', { class: 'pill pill--connecting' }, ['Checking…']);
  const linkInput = el('textarea', {
    class: 'input input--multiline',
    rows: '4',
    placeholder: 'Paste the camera embed link or an .m3u8 URL',
    'aria-label': 'Stream link',
    autocapitalize: 'none',
    autocorrect: 'off',
    spellcheck: 'false',
  });
  const saveButton = el('button', { class: 'button button--primary', type: 'button' }, ['Save link']);
  const currentUrl = el('p', { class: 'settings__current' }, ['—']);
  const diskLine = el('p', { class: 'settings__current' }, ['—']);

  const setHealth = (health: StreamHealthDto | null): void => {
    healthPill.className = `pill pill--${health === null ? 'connecting' : health.ok ? 'live' : 'unavailable'}`;
    healthPill.textContent = healthText(health);
  };

  const loadSettings = (): void => {
    void (async () => {
      try {
        const { data, meta } = await api.settings();
        currentUrl.textContent = `Current HD stream: ${data.streamUrlHD}`;
        setHealth(asHealth(meta?.['health']));
      } catch (err) {
        if (err instanceof ApiError && err.status === 401) throw err;
        currentUrl.textContent = 'Could not load settings.';
      }
    })();
  };

  const refreshHealth = (): void => {
    void (async () => {
      setHealth(null);
      try {
        setHealth(await api.streamHealth());
      } catch {
        setHealth({ ok: false, checkedAt: Date.now(), detail: 'Health check failed.' });
      }
    })();
  };

  const loadDisk = (): void => {
    void (async () => {
      try {
        const disk = await api.disk();
        diskLine.textContent = `${formatBytes(disk.freeBytes)} free of ${formatBytes(disk.totalBytes)} · recordings use ${formatBytes(disk.recordingsBytes)}`;
      } catch {
        diskLine.textContent = 'Disk usage unavailable.';
      }
    })();
  };

  saveButton.addEventListener('click', () => {
    const value = linkInput.value.trim();
    if (value === '') {
      toast('Paste a link first.', 'error');
      return;
    }

    saveButton.disabled = true;
    saveButton.textContent = 'Checking link…';

    void (async () => {
      try {
        const { data, meta } = await api.saveSettings({ streamLink: value });
        currentUrl.textContent = `Current HD stream: ${data.streamUrlHD}`;
        setHealth(asHealth(meta?.['health']));
        linkInput.value = '';
        toast('Stream link saved.');
      } catch (err) {
        toast(err instanceof ApiError ? err.message : 'Could not save that link.', 'error');
      } finally {
        saveButton.disabled = false;
        saveButton.textContent = 'Save link';
      }
    })();
  });

  const recheckButton = el('button', { class: 'button button--ghost', type: 'button' }, [
    'Re-check stream',
  ]);
  recheckButton.addEventListener('click', refreshHealth);

  const renewButton = el('button', { class: 'button button--ghost', type: 'button' }, [
    'Get a new token',
  ]);
  renewButton.addEventListener('click', () => {
    renewButton.disabled = true;
    renewButton.textContent = 'Renewing…';
    setHealth(null);

    void (async () => {
      try {
        const result = await api.refreshStream();
        setHealth(result.health);
        loadSettings();
        toast(
          result.health.ok
            ? result.changed
              ? 'Got a fresh token — the camera should work now.'
              : 'Token was still current; the stream is healthy.'
            : 'Renewed, but the camera is still unreachable.',
          result.health.ok ? 'info' : 'error',
        );
      } catch (err) {
        toast(err instanceof ApiError ? err.message : 'Could not renew the token.', 'error');
      } finally {
        renewButton.disabled = false;
        renewButton.textContent = 'Get a new token';
      }
    })();
  });

  const logoutButton = el('button', { class: 'button button--ghost', type: 'button' }, ['Log out']);
  logoutButton.addEventListener('click', () => {
    void (async () => {
      try {
        await api.logout();
      } catch {
        // Clearing the cookie server-side is best effort; reload regardless.
      }
      window.location.reload();
    })();
  });

  const logoutAllButton = el('button', { class: 'button button--danger', type: 'button' }, [
    'Log out all devices',
  ]);
  logoutAllButton.addEventListener('click', () => {
    if (!window.confirm('Log out every device? Everyone will need the password again.')) return;
    void (async () => {
      try {
        await api.logoutAll();
        window.location.reload();
      } catch (err) {
        toast(err instanceof ApiError ? err.message : 'Could not log out.', 'error');
      }
    })();
  });

  root.append(
    el('section', { class: 'settings' }, [
      el('div', { class: 'settings__group' }, [
        el('h2', { class: 'settings__heading' }, ['Camera stream']),
        healthPill,
        currentUrl,
        el('div', { class: 'settings__actions' }, [renewButton, recheckButton]),
        el('p', { class: 'settings__hint' }, [
          'The camera token expires every few hours; the app renews it automatically. Tap "Get a new token" if the picture is stuck.',
        ]),
        linkInput,
        el('div', { class: 'settings__actions' }, [saveButton]),
        el('p', { class: 'settings__hint' }, [
          'Only needed if automatic renewal stops working: ask the daycare for a fresh embed link and paste the whole thing here.',
        ]),
      ]),
      el('div', { class: 'settings__group' }, [
        el('h2', { class: 'settings__heading' }, ['Storage']),
        diskLine,
      ]),
      el('div', { class: 'settings__group' }, [
        el('h2', { class: 'settings__heading' }, ['Security']),
        el('div', { class: 'settings__actions' }, [logoutButton, logoutAllButton]),
        el('p', { class: 'settings__hint' }, [
          'Logging out all devices signs out every phone and computer, including this one.',
        ]),
      ]),
    ]),
  );

  loadSettings();
  loadDisk();
  refreshHealth();

  return () => undefined;
};
